import { AlertCircle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { OnboardingEdition } from '@/types';
import { DiscordGlyph } from './marks';
import { OnboardingShell, titleFont } from './OnboardingShell';

const KNOWN_ERRORS: Record<string, string> = {
  missing_code: 'Código de autorización faltante.',
  not_authenticated: 'No estás autenticado.',
  invalid_profile: 'Perfil inválido.',
  connection_failed:
    'Error al conectar con Discord. Por favor, intenta nuevamente.',
};

// _components/discord-connect-step.tsx. The Discord callback reports its failure in the
// query string, which the controller hands over as a prop (PORTING.md C6).
export function DiscordConnectStep({
  edition,
  discordOauthUrl,
  discordError,
}: {
  edition: OnboardingEdition;
  discordOauthUrl: string;
  discordError: string | null;
}) {
  return (
    <OnboardingShell ph26={edition.ph26} width="max-w-2xl">
      {/* Title */}
      <div className="text-center">
        <h1
          className={`inline-block font-bold ${titleFont(edition.ph26)} text-2xl text-primary sm:text-3xl md:text-4xl`}
        >
          Conectar{' '}
          <span className="bg-primary px-2 py-1 text-background">Discord</span>
        </h1>
        <p className="mt-4 font-mono text-primary/70 text-sm">
          Únete al servidor del hackathon
        </p>
      </div>

      {/* Connect Card */}
      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
        <div className="space-y-6">
          {/* Error Alert */}
          {discordError && (
            <Alert className="border-red-500 bg-red-50">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <AlertTitle className="text-red-800">
                Error al conectar Discord
              </AlertTitle>
              <AlertDescription className="text-red-700">
                {KNOWN_ERRORS[discordError] ?? `Error: ${discordError}`}
              </AlertDescription>
            </Alert>
          )}

          {/* Discord Icon */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="rounded-full bg-[#5865F2] p-8">
                <DiscordGlyph className="h-16 w-16 text-white" />
              </div>
              {/* Server Icon Overlapped */}
              {edition.discordServerIcon && (
                <div className="-bottom-2 -right-2 absolute rounded-full border-4 border-background bg-white p-1">
                  <img
                    src={edition.discordServerIcon}
                    alt="Platanus Hack Discord Server"
                    width={48}
                    height={48}
                    className="h-12 w-12 rounded-full"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="space-y-4 text-center">
            <h3
              className={`font-bold ${titleFont(edition.ph26)} text-primary text-xl`}
            >
              Conecta tu cuenta de Discord
            </h3>
            <p className="font-mono text-primary/80 text-sm">
              Discord es el medio de comunicación oficial durante la hack. Debes
              estar ahí para recibir toda la información importante. (Y también
              interactuar con hackers, mentores y sponsors)
            </p>
          </div>

          {/* Connect Button */}
          <div className="flex justify-center pt-4">
            <Button
              size="lg"
              className="w-full bg-[#5865F2] hover:bg-[#4752C4] sm:w-auto"
              onClick={() => {
                window.location.href = discordOauthUrl;
              }}
            >
              <div className="mr-2 flex items-center gap-1.5">
                <DiscordGlyph className="h-5 w-5" />
                {edition.discordServerIcon && (
                  <img
                    src={edition.discordServerIcon}
                    alt="Discord Server"
                    width={20}
                    height={20}
                    className="h-5 w-5 rounded"
                  />
                )}
              </div>
              Conectar Discord
            </Button>
          </div>
        </div>
      </div>
    </OnboardingShell>
  );
}
